import { useCallback, useEffect, useMemo, useState } from "react";
import { useCompanyScope } from "@/auth/companyScope";
import { Select, SearchInput } from "@/components/inputs";
import { Toolbar } from "@/components/Field";
import { PageHead } from "@/components/PageHead";
import { POLL } from "@/lib/format";
import { RQ } from "@/modules/registry/parts_common";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { registry, type RegDevice } from "@/api/client";
import { Chip } from "@/components/Chip";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Drawer } from "@/components/Drawer";
import { KV, Freshness } from "@/components/KV";
import { useConfirm } from "@/components/ConfirmDialog";
import { ago } from "@/lib/format";
import { downloadCsv } from "@/lib/download";
import { CAP, useSession } from "@/auth/session";

const STATE_CHIP: StatusMap = {
  active: { label: "ACTIVE", color: "var(--ok)" },
  suspended: { label: "SUSPENDED", color: "var(--warn)" },
  retired: { label: "RETIRED", color: "var(--t-mute)" },
};

/** Devices -> spreadsheet. The hand-rolled version this replaces joined values
 *  with a bare comma, so any field containing one -- a company name, most
 *  obviously -- silently split into two columns and shifted the rest of the
 *  row. `toCsv` quotes, and adds the BOM Excel needs to read UTF-8. */
function exportDevices(rows: RegDevice[], nameById: Record<string, string>) {
  downloadCsv("registry-devices.csv", rows.map((d) => ({
    imei: d.imei, state: d.state,
    company: nameById[d.tenant_id] ?? d.tenant_id,
    board_id: d.board_id ?? "", iccid: d.iccid ?? "",
    fw: d.app_ver ?? "", last_seen: d.last_seen ?? "",
  })), ["imei", "state", "company", "board_id", "iccid", "fw", "last_seen"]);
}

export function RegistryDevicesPage({ embedded = false }: { embedded?: boolean } = {}) {
  const PAGE = 100;
  const { company } = useCompanyScope();   // the app bar's (D-134)
  const [state, setState] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [company]);
  const [sel, setSel] = useState<string | null>(null);

  const companies = useQuery({ queryKey: [...RQ, "companies"], queryFn: registry.companies });
  const nameById = useMemo(() => {
    const m: Record<string, string> = {};
    (companies.data?.companies ?? []).forEach((c) => (m[c.company_id] = c.name));
    return m;
  }, [companies.data]);

  const q = useQuery({
    queryKey: [...RQ, "devices", company, state, search, page],
    queryFn: () => registry.devices({
      company: company || undefined, state: state || undefined, search: search || undefined,
      limit: String(PAGE), offset: page ? String(page * PAGE) : undefined,
    }),
    refetchInterval: POLL.LIST,
  });
  const devs = q.data?.devices ?? [];
  const total = q.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  const onRow = useCallback((d: RegDevice) => setSel(d.imei), []);
  const dim = <span style={{ color: "var(--t-dim)" }}>—</span>;
  const devCols = useMemo<DataCol<RegDevice>[]>(() => [
    { key: "imei", priority: 1, label: "IMEI", width: 150, mono: true, render: (d) => <span className="dev">{d.imei}</span> },
    { key: "state", priority: 2, label: "State", width: 110, render: (d) => <StatusChip st={d.state} map={STATE_CHIP} /> },
    { key: "company", priority: 2, label: "Company", width: 150, render: (d) => nameById[d.tenant_id] ?? d.tenant_id },
    { key: "board", priority: 2, label: "Board", width: 110, mono: true, render: (d) => d.board_id ?? dim },
    { key: "iccid", priority: 3, label: "SIM (ICCID)", width: 170, mono: true, render: (d) => d.iccid ?? dim },
    { key: "fw", priority: 3, label: "FW", width: 60, render: (d) => d.app_ver ?? dim },
    { key: "seen", priority: 2, label: "Last seen", width: 90, render: (d) => <Freshness ts={d.last_seen} /> },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [nameById]);

  return (
    <div className={embedded ? undefined : "mpage on"}>
      {!embedded && <PageHead eyebrow="Registry" title="Connections" />}

      <Toolbar style={{ margin: "18px 0 12px" }}
        right={<button className="btn ghost" onClick={() => exportDevices(devs, nameById)} title="Export this page of the filtered list as CSV">⭳ Download list</button>}>
        <SearchInput placeholder="Search IMEI or board…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
        {/* No "Inventory (unallocated)" bucket and no Inventory state: both
            asked for `tenant_id IS NULL`, which D-83 made impossible, so each
            could only ever answer "no devices match these filters"a filter
            that is always empty reads as a broken page. Whether a device is in
            stock is the bound PART's stage, on the Inventory page. */}
        <Select style={{ minWidth: 140 }} value={state} onChange={(e) => { setState(e.target.value); setPage(0); }}>
          <option value="">All states</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="retired">Retired</option>
        </Select>
      </Toolbar>

      <DataTable rows={devs} cols={devCols} rowKey={(d) => d.imei} onRowClick={onRow}
        maxHeight={560} stickyFirst
        empty={<div className="panel panel-pad" style={{ color: "var(--t-dim)", fontSize: 12 }}>No devices match these filters.</div>} />

      {total > PAGE && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end", marginTop: 12 }}>
          <span style={{ fontSize: 11.5, color: "var(--t-mute)" }}>
            {page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of {total}
          </span>
          <button className="btn ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Prev</button>
          <button className="btn ghost" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>Next →</button>
        </div>
      )}

      {sel && <DeviceDrawer imei={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

function DeviceDrawer({ imei, onClose }: { imei: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [...RQ, "device", imei], queryFn: () => registry.device(imei), refetchInterval: POLL.STATE });
  const { can: canDo } = useSession();
  const revoke = useMutation({
    mutationFn: () => registry.revoke(imei),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [...RQ, "device", imei] }); void qc.invalidateQueries({ queryKey: [...RQ, "devices"] }); },
  });
  const d = q.data;
  const { ask, dialog } = useConfirm();

  return (
    <Drawer onClose={onClose}
      header={
        <div>
          <div className="mono" style={{ fontSize: 15, fontWeight: 600, color: "var(--t-bright)" }}>{imei}</div>
          {d && <div style={{ marginTop: 5, display: "flex", gap: 6 }}>
            <StatusChip st={d.state} map={STATE_CHIP} />
            <Chip tone="mute">{d.tenant_id}</Chip>
          </div>}
        </div>
      }>
      <>
          {!d ? <div className="muted">Loading…</div> : <>
            <div className="section-label flush">Current facts</div>
            <KV k="Board" v={d.board_id} mono />
            <KV k="SIM (ICCID)" v={d.iccid} mono />
            <KV k="Firmware" v={d.app_ver != null ? `v${d.app_ver} · ${d.mode ?? "?"}` : null} />
            <KV k="Last seen" v={ago(d.last_seen)} />
            <KV k="Deployment" v={`seq ${d.deployment_seq}`} />

            <div className="section-label">Security</div>
            <KV k="Broker credential" v={d.credential ? "provisioned" : "none"} />
            {d.credential && <KV k="Type" v={d.credential.kind} />}
            {/* REVOKE_CREDENTIAL is internal-only: this kills the device at
                the broker, so it is a platform operation, not a fleet one. */}
            {canDo(CAP.REVOKE_CREDENTIAL) && (
              <div style={{ marginTop: 10 }}>
                <button className="btn red" onClick={() => ask(`Revoke ${imei} at the broker? It won't be able to connect.`, () => revoke.mutate(), "Revoke")}>Revoke</button>
              </div>
            )}

            <div className="section-label">Life journey</div>
            <div style={{ position: "relative", paddingLeft: 20 }}>
              <div style={{ position: "absolute", left: 5, top: 4, bottom: 4, width: 2, background: "var(--line2)" }} />
              {d.events.map((e, i) => (
                <div key={i} style={{ position: "relative", paddingBottom: 16 }}>
                  <span style={{ position: "absolute", left: -19, top: 3, width: 10, height: 10, borderRadius: "50%", background: e.event === "board_conflict" ? "var(--warn)" : "var(--teal)", border: "2px solid var(--surface)" }} />
                  <div style={{ fontSize: 10.5, color: "var(--t-dim)", fontWeight: 700 }}>{e.ts ? new Date(e.ts).toLocaleString() : ""}</div>
                  <div style={{ fontSize: 12.5, color: "var(--t-bright)", fontWeight: 600 }}>{e.event}{e.field ? ` · ${e.field}` : ""}</div>
                  <div style={{ fontSize: 11.5, color: "var(--t-mute)" }}>
                    {e.old != null && e.new != null ? `${e.old || "—"} → ${e.new}` : ""}{e.source ? ` · ${e.source}` : ""}{e.actor ? ` · ${e.actor}` : ""}
                  </div>
                </div>
              ))}
              {d.events.length === 0 && <div style={{ color: "var(--t-dim)", fontSize: 12 }}>No events yet.</div>}
            </div>
          </>}
      {dialog}
      </>
    </Drawer>
  );
}

